import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, async (event) => {
      const newMediaGallery = event.detail.mediaGallery;
      const colorOption = event.detail.variant?.option1;

      let targetSlideIndex = -1;
      if (colorOption) {
        const slides = newMediaGallery.querySelectorAll('slideshow-slide');

        slides.forEach((slide, i) => {

          const img = slide.querySelector('img.product-media__image');
          if ((img?.getAttribute('alt') || '').trim() === colorOption) targetSlideIndex = i;
        });
      }

      this.replaceWith(newMediaGallery);

      if (targetSlideIndex > 0) {
        requestAnimationFrame(() => {
          setTimeout(() => {
            requestAnimationFrame(() => {
              setTimeout(() => {
                const slideshow = newMediaGallery.querySelector('slideshow-component');
                slideshow?.select(targetSlideIndex, undefined, { animate: false });
              }, 0);
            });
          }, 0);
        });
      }
    }, { signal });
  }




  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
